import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Loading from './Loading';
import { Container, Typography } from '@mui/material';

import MuiDialog from 'components/MuiDialog';
import {
  SurveyDialogProps,
  TCoreNursingSkillVideoDefaultValues,
} from 'routes/Main/Survey/type';
import useSurvey from 'store/survey/useSurvey';
import useNotification from 'hooks/useNotification';
import useUser from 'store/user/useUser';
import useStudent from 'store/student/useStudent';

import VideoForm from './VideoForm';
import StudentInfo from './StudentModeInfo';
import ProfModeInfo from './ProfModeInfo';

import axios from 'axios';
import { authHeaders, handleUnauthorizedError } from 'apis/authToken';

const CoreNursingSkillVideo = (
  props: SurveyDialogProps<TCoreNursingSkillVideoDefaultValues>
) => {
  const [refresh, setRefresh] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const { isStudent } = useUser();
  const { student_uuid } = useStudent();
  const [loading, setLoading] = useState(false);

  const {
    title,
    isOpen,
    disabled,
    defaultValues,
    user_id,
    nurseName,
    patientInfo,
    onClose,
  } = props;

  const { onUpdateIsSave } = useSurvey();
  const { onSuccess, onFail, onResultCode, onRequired } = useNotification();

  const { handleSubmit, register, getValues, setValue, watch } = useForm({
    defaultValues,
  });

  const onSubmit = async (data: TCoreNursingSkillVideoDefaultValues) => {
    setLoading(true);

    // 새로 고른 칸만 그 칸 번호(file1~3)로 보낸다. 서버는 번호 칸에 저장한다 (ENR-BUGFIX-131)
    const formData = new FormData();
    let toSaveCount = 0;
    (data.files ?? []).forEach((slot, i) => {
      if (slot && !slot.saved && slot.file instanceof File) {
        formData.append(`file${i + 1}`, slot.file);
        toSaveCount++;
      }
    });
    if (toSaveCount === 0) {
      onUpdateIsSave(true);
      setLoading(false);
      onSuccess('저장되었습니다');
      return;
    }

    try {
      const res = await axios
        .post(
          `${process.env.REACT_APP_API_URL}/file/video_upload?user_id=${user_id}&patient_id=${patientInfo.patient_id}`,
          formData,
          // Content-Type 은 브라우저가 boundary 와 함께 정한다
          { headers: { Accept: 'application/json', ...authHeaders() } }
        )
        .catch(handleUnauthorizedError);
      if (res.data.rc === 1) {
        onUpdateIsSave(true);
        onSuccess('저장되었습니다');
        setRefresh(!refresh);
      } else {
        onResultCode(res.data.rc);
      }
    } catch (error) {
      onFail('저장에 실패하였습니다', error);
    }
    // 로딩 중 VideoForm 이 빠졌다가 서버 목록으로 다시 그려져 고른 파일이 화면에서 사라진다.
    // 성공·실패와 상관없이 폼 값도 비워 다음 저장 때 보이지 않는 파일을 다시 보내지 않게 한다
    setValue('files', []);
    setLoading(false);
  };

  const formProps = {
    disabled,
    watch,
    register,
    getValues,
    setValue,
    onSuccess,
    onRequired,
  };

  return (
    <MuiDialog.SurveyFormTwo
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      update_at={defaultValues?.update_at}
    >
      <Container maxWidth="md" sx={{ mt: 7.5, mb: 9, height: '350px' }}>
        {isStudent ? (
          <StudentInfo totalSize={totalSize} />
        ) : (
          <ProfModeInfo totalSize={totalSize} />
        )}
        {loading ? (
          <Loading />
        ) : (
          <VideoForm
            {...formProps}
            user_id={isStudent ? user_id : student_uuid}
            patient_id={patientInfo.patient_id}
            patient_name={patientInfo.name}
            totalSize={totalSize}
            setTotalSize={setTotalSize}
            refresh={refresh}
          />
        )}
        <div style={{ paddingBottom: '50px' }}>
          <Typography
            sx={{
              whiteSpace: 'nowrap',
              fontWeight: 400,
              borderTop: '0.5px solid lightGray',
              lineHeight: '50px',
            }}
          >
            💡 핵심간호술기영상 저장이 안돼요 !
          </Typography>
          <Typography
            sx={{
              whiteSpace: 'pre-wrap',
              width: '900px',
              fontSize: '14px',
              height: '80px',
            }}
          >
            영상 저장이 안 되는 경우는, 동영상이 500MB 이하인지 확인해 주세요!
            <br />
            500MB 이상인 경우 구글에 “동영상 크기 줄이기”를 검색해 사이트를
            이용하거나, 혹은 카카오톡으로 한번 전송해 용량을 줄이는 것을
            추천드립니다 :)
          </Typography>
        </div>

        {/* <VideoForm {...formProps} user_id={isStudent ? user_id : student_uuid} patient_id={patientInfo.patient_id} patient_name={patientInfo.name} totalSize={totalSize} setTotalSize={setTotalSize} refresh={refresh} /> */}
      </Container>
    </MuiDialog.SurveyFormTwo>
  );
};

export default CoreNursingSkillVideo;
